import { MinusCircleOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Skeleton,
  Space,
  Table,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { errorMessage } from '../../api/client';
import { fetchFixedWithdrawalImageUrl, useAddFixedWithdrawal, useFixedAccount } from '../../api/hooks';
import type { FixedWithdrawal, PartyType } from '../../types';
import { apiDate, date, money } from '../../utils/format';
import { paymentMethodOptions } from '../../utils/paymentMethods';
import PendingDocumentPicker from '../investor/PendingDocumentPicker';
import TransactionImageLink from '../investor/TransactionImageLink';
import TransactionAccountField from './TransactionAccountField';
import { useTransactionAccountField } from '../../utils/transactionAccountField';
import QuickShareButton from './QuickShareButton';
import { usePartyContact } from './usePartyContact';
import { transactionMessage } from '../../utils/quickShare';

interface Props {
  partyType: PartyType;
  partyId: number;
  canWrite: boolean;
}

/**
 * Money coming back against a fixed-return investment. Every withdrawal names the investment it
 * settles, because each one carries its own return date — an unattributed payment would leave no
 * way to tell which obligation had been met.
 */
export default function FixedWithdrawalsTab({ partyType, partyId, canWrite }: Props) {
  const { data: account, isLoading, error } = useFixedAccount(partyType, partyId);
  const addWithdrawal = useAddFixedWithdrawal(partyType, partyId);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [transactionImage, setTransactionImage] = useState<File | null>(null);
  const accountField = useTransactionAccountField(partyType, partyId);
  const contact = usePartyContact(partyType, partyId);

  const selectedId = Form.useWatch('fixedInvestmentId', form) as number | undefined;
  const amount = Form.useWatch('amount', form) as number | undefined;
  const paymentMethod = Form.useWatch('paymentMethod', form) as string | undefined;

  if (error) return <Alert type="error" showIcon message={errorMessage(error)} />;
  if (isLoading || !account) return <Skeleton active paragraph={{ rows: 6 }} />;

  // Only investments with something still owed can be drawn against.
  const openInvestments = account.investments.filter((i) => i.remainingAmount > 0);
  const selected = openInvestments.find((i) => i.id === selectedId);

  const options = openInvestments.map((i) => ({
    value: i.id,
    label: `#${i.id} — ${date(i.investDate)} — remaining ${money(i.remainingAmount)} — due ${date(i.dueDate)}`,
  }));

  // Capped at the account's total remaining, not just the chosen investment's: if the amount
  // exceeds what is left on the selected tranche, the excess spills into the other open tranches,
  // oldest first — the API enforces the same bound and allocation independently.
  const overdrawn = !!amount && amount > account.totalRemaining;
  const spillsOver = !!selected && !!amount && amount > selected.remainingAmount && !overdrawn;

  const onFinish = (values: Record<string, unknown>) => {
    const investment = openInvestments.find((i) => i.id === values.fixedInvestmentId);
    const entered = values.amount as number;
    const spills = !!investment && entered > investment.remainingAmount;

    Modal.confirm({
      title: 'Confirm withdrawal',
      content: investment
        ? spills
          ? `Record ${money(entered)} withdrawn? Investment #${investment.id} only has ${money(
              investment.remainingAmount,
            )} left, so the remaining ${money(entered - investment.remainingAmount)} will be drawn from the next oldest open investment(s). Account remaining after: ${money(
              account.totalRemaining - entered,
            )}.`
          : `Record ${money(entered)} withdrawn from investment #${investment.id}? ${money(
              investment.remainingAmount - entered,
            )} would remain on it.`
        : `Record ${money(entered)} withdrawn?`,
      okText: 'Confirm withdrawal',
      onOk: async () => {
        try {
          await addWithdrawal.mutateAsync({
            fixedInvestmentId: values.fixedInvestmentId as number,
            amount: entered,
            withdrawDate: apiDate(values.withdrawDate as dayjs.Dayjs)!,
            paymentMethod: values.paymentMethod as string | undefined,
            notes: values.notes as string | undefined,
            transactionImage,
          });
          await accountField.commitIfChanged();
          message.success('Withdrawal recorded successfully.');
          form.resetFields();
          setTransactionImage(null);
          setOpen(false);
        } catch (err) {
          // Shown as a modal, not a toast: a toast fires behind the confirm dialog's mask and is
          // easy to miss entirely, which leaves the action looking like it silently did nothing.
          Modal.error({ title: 'Could not record the withdrawal', content: errorMessage(err) });
        }
      },
      onCancel: () => message.info('Withdrawal cancelled.'),
    });
  };

  return (
    <>
      {canWrite && (
        <Space style={{ marginBottom: 16 }}>
          <Button
            danger
            type="primary"
            icon={<MinusCircleOutlined />}
            disabled={options.length === 0}
            onClick={() => {
              form.setFieldsValue({ withdrawDate: dayjs() });
              setOpen(true);
            }}
          >
            Withdraw
          </Button>
        </Space>
      )}

      <Descriptions bordered size="small" column={{ xs: 1, md: 4 }} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Total invested">{money(account.totalInvested)}</Descriptions.Item>
        <Descriptions.Item label="Total return agreed">{money(account.totalReturnAgreed)}</Descriptions.Item>
        <Descriptions.Item label="Total withdrawn">{money(account.totalWithdrawn)}</Descriptions.Item>
        <Descriptions.Item label="Remaining to return">
          <strong>{money(account.totalRemaining)}</strong>
        </Descriptions.Item>
      </Descriptions>

      <Table<FixedWithdrawal>
        rowKey="id"
        dataSource={account.withdrawals}
        scroll={{ x: 'max-content' }}
        columns={[
          { title: '#', dataIndex: 'id', width: 70 },
          { title: 'Date', dataIndex: 'withdrawDate', render: date },
          { title: 'From investment', dataIndex: 'fixedInvestmentId', render: (v: number) => `#${v}` },
          { title: 'Amount', dataIndex: 'amount', align: 'right', render: money },
          { title: 'Remaining after', dataIndex: 'remainingAfter', align: 'right', render: money },
          { title: 'Payment method', dataIndex: 'paymentMethod', render: (v: string | null) => v ?? '—' },
          { title: 'Notes', dataIndex: 'notes', render: (v: string | null) => v ?? '—' },
          {
            title: 'Transaction proof',
            render: (_, row) => (
              <TransactionImageLink
                exists={row.hasTransactionImage}
                title={`Transaction proof — withdrawal #${row.id}`}
                fetchUrl={() => fetchFixedWithdrawalImageUrl(partyType, partyId, row.id)}
              />
            ),
          },
          ...(contact.ready
            ? [
                {
                  title: 'Notify',
                  render: (_: unknown, row: FixedWithdrawal) => (
                    <QuickShareButton
                      phone={contact.phone}
                      recipientName={contact.name}
                      message={transactionMessage(contact, 'Withdrawal', row.amount, row.withdrawDate)}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />

      <Modal
        title="Withdraw"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={addWithdrawal.isPending}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} preserve={false}>
          <Form.Item name="fixedInvestmentId" label="From investment" rules={[{ required: true }]}>
            <Select options={options} placeholder="Select an investment" />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Amount (PKR)"
            rules={[
              { required: true },
              { type: 'number', min: 0.01, message: 'Amount must be greater than zero.' },
              () => ({
                validator: (_, value: number) => {
                  if (value === undefined || value === null) return Promise.resolve();
                  return value <= account.totalRemaining
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          `Only ${money(account.totalRemaining)} is left across this account. Enter ${money(
                            account.totalRemaining,
                          )} or less.`,
                        ),
                      );
                },
              }),
            ]}
          >
            <InputNumber min={0.01} max={account.totalRemaining} style={{ width: '100%' }} />
          </Form.Item>

          {selected && (
            <Alert
              type={overdrawn ? 'error' : spillsOver ? 'warning' : 'info'}
              showIcon
              style={{ marginBottom: 16, marginTop: -8 }}
              message={
                overdrawn
                  ? `Only ${money(account.totalRemaining)} is left across this account.`
                  : spillsOver
                    ? `Investment #${selected.id} has ${money(selected.remainingAmount)} left · the remaining ${money(
                        (amount ?? 0) - selected.remainingAmount,
                      )} will be drawn from the next oldest open investment(s) · account remaining after ${money(
                        account.totalRemaining - (amount ?? 0),
                      )}`
                    : `Investment #${selected.id} has ${money(selected.remainingAmount)} left` +
                      (amount ? ` · ${money(selected.remainingAmount - amount)} would remain` : '') +
                      ` · account remaining ${money(account.totalRemaining)}`
              }
            />
          )}

          <Form.Item name="withdrawDate" label="Withdrawal date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="paymentMethod" label="Payment method">
            <Select placeholder="How the money moved" allowClear options={paymentMethodOptions} />
          </Form.Item>
          <TransactionAccountField field={accountField} paymentMethod={paymentMethod} />
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>

          <PendingDocumentPicker
            type="withdrawal-image"
            label="Transaction proof (optional)"
            hint="Receipt or transfer screenshot · JPG, PNG or WebP · max 5 MB"
            file={transactionImage}
            onChange={setTransactionImage}
          />
        </Form>
      </Modal>
    </>
  );
}
